import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Form, Input, Select } from "antd";
import { GetPaymentGatewayApi } from "../../../../api/request/wallet/deposit";
import { GetCurrentCurrencyRateApi } from "../../../../api/request/wallet/cashdeposit";
import UpiDeposit from "./components/UpiDeposit";
import UsdtTrc20 from "./components/UsdtTrc20";
import Stripe from "./components/Stripe";
import CreditDebitCard from "./components/CreditDebitCard";
import ZaroPay from "./components/ZaroPay";
import { GetAccountsApi } from "../../../../api/request/transfer";
import DepositRequest from "./DepositRequest";
import CashDepositMobile from "./components/CashDepositMobile.jsx";
import PayPal from "./components/PayPal.jsx";
import ZinaCardPayment from "./components/ZinaCardPayment.jsx";
import UniPayment from "./components/Unipayment.jsx";
import Balance from "../../../../assets/Balance.jsx";
import Tamara from "./components/Tamara.jsx";

const DepositMobile = ({ setPaymentMethods }) => {
  const [form] = Form.useForm();
  const [paymentGateways, setPaymentGateways] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [amount, setAmount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [step, setStep] = useState(1);
  const [isRefresh, setIsRefresh] = useState(false);
  const [isCashDeposit, setIsCashDeposit] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState("wallet");
  const [currencyRate, setCurrencyRate] = useState(null);
  const [validation, setValidation] = useState("");

  const parseAmountLimit = (val) => {
    if (val == null || val === "") return NaN;
    if (typeof val === "number") return val;
    return parseFloat(String(val).replace(/[^0-9.]/g, ""));
  };

  const getAmountError = (usdAmount, method) => {
    if (usdAmount === undefined || usdAmount === null || usdAmount === "") {
      return "Please enter amount.";
    }
    const num = Number(usdAmount);
    if (isNaN(num) || num <= 0) {
      return "Please enter a valid amount.";
    }
    const min = parseAmountLimit(method?.minAmount);
    const max = parseAmountLimit(method?.maxAmount);
    if (!isNaN(min) && num < min) {
      return `Amount must be at least $${min}.`;
    }
    if (!isNaN(max) && num > max) {
      return `Amount cannot exceed $${max}.`;
    }
    return "";
  };

  const getPaymentGateway = async () => {
    setIsLoading(true);
    try {
      const { data } = await GetPaymentGatewayApi();
      if (data?.success) setPaymentGateways(data?.data);
    } catch (error) {
      if (error?.response?.data?.error?.message)
        toast.error(error?.response?.data?.error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const getAccounts = async () => {
    setIsLoading(true);
    try {
      const { data } = await GetAccountsApi(
        new URLSearchParams({ FilterText: "Approved" }).toString()
      );
      if (data?.success) {
        setAccounts(data?.data?.filter((account) => account?.isLive));
      }
    } catch (error) {
      if (error?.response?.data?.error?.message)
        toast.error(error?.response?.data?.error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const getCurrentCurrencyRate = async () => {
    try {
      const { data } = await GetCurrentCurrencyRateApi();
      if (data?.success) setCurrencyRate(data?.data);
    } catch (error) {
    }
  };

  useEffect(() => {
    getPaymentGateway();
    getAccounts();
    getCurrentCurrencyRate();
  }, []);

  // Set default payment method to first option when paymentGateways is loaded
  useEffect(() => {
    if (paymentGateways.length > 0 && !paymentMethod) {
      setPaymentMethod(paymentGateways[0]);
      setPaymentMethods(paymentGateways[0]);
    }
  }, [paymentGateways]);
  const validateAmount = (_, value) => {
    const checkValue = paymentMethod?.name === "Tamara" ? amount : value;
    const error = getAmountError(checkValue, paymentMethod);
    setValidation(error);
    if (error) {
      return Promise.reject(new Error(error));
    }
    return Promise.resolve();
  };

  useEffect(() => {
    setValidation(getAmountError(amount, paymentMethod));
  }, [amount, paymentMethod]);

  const handleSubmit = (values) => {
    if ((values?.amount || values?.inrAmount) && paymentMethod) {
      setStep(2);
    }
  };

  const resetAll = () => {
    form.resetFields();
    setAmount(0);
    setPaymentMethod(null);
    setStep(1);
    setIsRefresh(true);
  };

  useEffect(() => {
    if (!paymentMethod) return;

    form.resetFields(["amount", "inrAmount"]);
    setAmount(0);

    if (
      paymentMethod?.name === "Cash Deposit" ||
      paymentMethod?.name === "Bank Transfer"
    ) {
      setIsCashDeposit(true);
    } else {
      setIsCashDeposit(false);
    }

    setStep(1);
  }, [paymentMethod]);

  const renderStep = () => {
    switch (paymentMethod?.name) {
      case "Bank Transfer":
        return <CashDepositMobile amount={amount} resetAll={resetAll} loginTerminalId={selectedAccount} validateAmount={validation} />;
      case "UPI":
        return (
          <UpiDeposit
            amount={amount}
            paymentMethod={paymentMethod}
            resetAll={resetAll}
            loginTerminalId={selectedAccount}
            validateAmount={validation}
          />
        );
      case "USDT-TRC20":
        return (
          <UsdtTrc20
            amount={amount}
            paymentMethod={paymentMethod}
            resetAll={resetAll}
            loginTerminalId={selectedAccount}
            validateAmount={validation}
          />
        );
      case "Debit/Credit Card":
        return (
          <Stripe
            amount={amount}
            paymentMethod={paymentMethod}
            accounts={accounts}
            resetAll={resetAll}
            loginTerminalId={selectedAccount}
            validateAmount={validation}
          />
        );
      case "Network":
        return (
          <CreditDebitCard
            amount={amount}
            paymentMethod={paymentMethod}
            accounts={accounts}
            resetAll={resetAll}
            loginTerminalId={selectedAccount}
            validateAmount={validation}
          />
        );
      case "PayPal":
        return (
          <PayPal
            amount={amount}
            paymentMethod={paymentMethod}
            accounts={accounts}
            resetAll={resetAll}
            loginTerminalId={selectedAccount}
            validateAmount={validation}
          />
        );
      case "Crypto":
        return (
          <ZaroPay
            amount={amount}
            paymentMethod={paymentMethod}
            accounts={accounts}
            resetAll={resetAll}
            loginTerminalId={selectedAccount}
            validateAmount={validation}
          />
        );
      case "Easypaisa":
        return (
          <ZinaCardPayment
            amount={amount}
            resetAll={resetAll}
            QrCode={paymentMethod?.qrCodeUrl}
            loginTerminalId={selectedAccount}
            validateAmount={validation}
          />
        );
      case "Credit/Debit Cards":
      case "Crypto currencies":
        return (
          <UniPayment
            amount={amount}
            paymentMethod={paymentMethod}
            accounts={accounts}
            resetAll={resetAll}
            loginTerminalId={selectedAccount}
            validateAmount={validation}
          />
        );
      case "Tamara":
        return <Tamara amount={amount} paymentMethod={paymentMethod} accounts={accounts} resetAll={resetAll} loginTerminalId={selectedAccount} validateAmount={validation} />;
      default:
        return null;
    }
  };

  const isTamara = paymentMethod?.name === "Tamara";

  return (
    <div className="mobile-dashboard min-h-screen">
      <div className="mb-4">
        <div className="relative">
          <Form
            form={form}
            layout="vertical"
            className="space-y-5"
            onFinish={handleSubmit}
          >
            <div>
              <label className="text-lg font-medium text-gray-700 block mb-2">Select Payment Method</label>
              <div className="grid grid-cols-2 gap-2.5">
                {paymentGateways.map((item) => {
                  const isSelected = paymentMethod?.name === item?.name;
                  return (
                    <button
                      key={item?.id}
                      type="button"
                      onClick={() => {
                        const selected = paymentGateways.find(
                          (pg) => pg.name === item.name
                        );
                        setPaymentMethod(selected);
                        setPaymentMethods(selected);
                      }}
                      className={`flex items-center gap-2.5 p-3 rounded-xl border-2 bg-white transition-all duration-200 text-left ${
                        isSelected
                          ? "border-primary shadow-sm"
                          : "border-gray-200"
                      }`}
                    >
                      <img
                        src={item?.paymentLogoUrl}
                        alt={item?.name}
                        className="h-8 w-8 object-contain flex-shrink-0"
                      />
                      <span className={`font-semibold text-xs leading-tight flex-1 ${isSelected ? "text-primary" : "text-gray-800"}`}>
                        {item?.name}
                      </span>
                      <span
                        className={`flex-shrink-0 h-4 w-4 rounded-full border-2 flex items-center justify-center ${
                          isSelected
                            ? "border-primary bg-primary"
                            : "border-gray-300 bg-white"
                        }`}
                      >
                        {isSelected && (
                          <span className="h-1.5 w-1.5 rounded-full bg-white" />
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* put where to deposit here */}
            <div className="mb-4">
              <label className="text-lg font-medium text-gray-700 block mb-2">Where to Deposit</label>
              <Select
                size="large"
                placeholder="Select account to deposit"
                className="w-full"
                onChange={(value) => setSelectedAccount(value)}
                value={selectedAccount}
              >
                <Select.Option value="wallet">
                  <div className="flex items-center gap-3 py-1">
                    <Balance color="black" className="size-6" />
                    <div className="flex items-center">
                      <span className="text-base font-medium">Wallet</span>
                      <span className="text-sm text-gray-500 ms-2">$0</span>
                    </div>
                  </div>
                </Select.Option>
                {accounts?.map((item, index) => (
                  <Select.Option key={index} value={item?.terminalID}>
                    <div className="flex items-center gap-3 py-1">
                      <img
                        src="/assets/images/vertex_logo.png"
                        alt="Icon"
                        className="h-6 w-6 object-contain"
                      />
                      <div className="flex items-center">
                        <span className="text-base font-medium">{item?.terminalID}</span>
                        <span className="text-sm text-gray-500 ms-2">${item?.balance || 0}</span>
                      </div>
                    </div>
                  </Select.Option>
                ))}
              </Select>
            </div>


            <Form.Item
              name="inrAmount"
              label={
                isTamara ? (
                  <span className="text-lg text-gray-500 font-medium">
                    Deposit Amount{" "}
                    <span className="primary">
                      ({`$1 = ${currencyRate?.targetCurrencyAEDRate || 0} AED`})
                    </span>
                  </span>
                ) : (
                  <span className="text-lg text-gray-500 font-medium">
                    Amount In USD
                  </span>
                )
              }
              rules={[{ validator: validateAmount }]}
            >
              {isTamara ? (
                <Input
                  type="number"
                  placeholder="0"
                  size="large"
                  prefix={
                    <span className="flex items-center gap-2">
                      <span className="primary">AED</span>
                      <span className="primary font-semibold">|</span>
                    </span>
                  }
                  addonAfter={`$ ${Number(amount)?.toFixed(2) || "0.00"}`}
                  onChange={(e) => {
                    const value = parseFloat(e?.target?.value) || 0;
                    const rate = Number(currencyRate?.targetCurrencyAEDRate || 0);
                    const convertedAmount = rate ? value / rate : 0;
                    setAmount(Number(convertedAmount.toFixed(2)));
                  }}
                />
              ) : (
                <Input
                  type="number"
                  prefix="$"
                  placeholder="Amount in USD"
                  size="large"
                  onChange={(e) => setAmount(parseFloat(e?.target?.value) || 0)}
                />
              )}
            </Form.Item>


            {/* Where to Deposit Dropdown (copied from PaymentDetails) */}

          </Form>
        </div>
      </div>

      {paymentMethod && renderStep()}

      <div className="mt-5">
        <DepositRequest isRefresh={isRefresh} setIsRefresh={setIsRefresh} />
      </div>
    </div>
  );
};

export default DepositMobile;
